import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, Mail, X } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    if (hashParams.get('type') === 'recovery') {
      setIsRecoveryMode(true);
      toast.info('Set your new password below.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRecoveryMode) {
        if (password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }

        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;

        await supabase.auth.signOut();
        setIsRecoveryMode(false);
        setPassword('');
        setConfirmPassword('');
        window.location.hash = '';
        toast.success('Password updated successfully. Please sign in.');
        navigate('/login', { replace: true });
      } else {
        // Login
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (error) throw error;

        // Check if user is active
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('is_active')
          .eq('id', data.user.id)
          .single();

        if (userError) {
          // If error reading user data (likely RLS blocking inactive user)
          await supabase.auth.signOut();
          toast.error('Your account has been deactivated. Please contact an administrator.');
          setLoading(false);
          return;
        }

        if (userData && userData.is_active === false) {
          // User is marked as inactive
          await supabase.auth.signOut();
          toast.error('Your account has been deactivated. Please contact an administrator.');
          setLoading(false);
          return;
        }

        toast.success('Welcome back!');
        navigate('/tasks');
      }
    } catch (error) {
      toast.error(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const openForgotPasswordModal = () => {
    setShowForgotModal(true);
  };



  return (
    <div className="min-h-screen flex items-center justify-center bg-black p-4" data-testid="login-page">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Login Card */}
      <div className="relative w-full max-w-md" style={{ animation: 'slideUp 0.5s ease-out' }}>
        <div className="glass-card p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex flex-col items-center justify-center gap-3 mb-4">
              <img
                src="/logo.png"
                alt="Cortex logo"
                className="w-14 h-14 object-contain"
              />
              <h1 className="brand-wordmark text-4xl leading-none">
                CORTEX
              </h1>
            </div>
            <p className="text-gray-400 mt-2">
              {isRecoveryMode ? 'Create your new password' : 'Welcome back'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isRecoveryMode && (
              <div>
                <label className="block text-base font-medium mb-3 text-gray-200">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={20} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full glass-input pl-12 pr-4"
                    style={{ minHeight: '50px', paddingLeft: '48px' }}
                    placeholder="Enter your email"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <label className="block text-base font-medium mb-3 text-gray-200">
                {isRecoveryMode ? 'New Password' : 'Password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full glass-input pr-12"
                  style={{ minHeight: '50px', paddingLeft: '48px', paddingRight: '48px' }}
                  placeholder={isRecoveryMode ? 'Enter your new password' : 'Enter your password'}
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-all duration-200 z-10 p-1 rounded-lg hover:bg-white/10"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {isRecoveryMode && (
              <div>
                <label className="block text-base font-medium mb-3 text-gray-200">Confirm New Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={20} />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full glass-input pr-12"
                    style={{ minHeight: '50px', paddingLeft: '48px', paddingRight: '48px' }}
                    placeholder="Confirm your new password"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-all duration-200 z-10 p-1 rounded-lg hover:bg-white/10"
                    data-testid="toggle-confirm-password"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            )}

            {!isRecoveryMode && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={openForgotPasswordModal}
                  className="text-sm text-gray-300 hover:text-white underline underline-offset-2"
                  data-testid="forgot-password-btn"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed mt-8"
              style={{ minHeight: '56px' }}
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </span>
              ) : (
                isRecoveryMode ? 'Save New Password' : 'Sign In'
              )}
            </button>
          </form>
        </div>
      </div>

      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-300">
          <div className="glass-card w-full max-w-sm p-8 relative" style={{ animation: 'slideUp 0.3s ease-out' }}>
            <button
              onClick={() => setShowForgotModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-white/10"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/10">
                <Lock className="text-white/60" size={28} />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-4">Access Assistance</h2>
              <div className="space-y-4">
                <p className="text-gray-300 text-lg leading-relaxed">
                  Kindly contact the head of the Tech Team to reset your credentials.
                </p>
                <p className="text-gray-400 text-sm">
                  This ensures secure and verified access to the platform.
                </p>
              </div>
              <button
                onClick={() => setShowForgotModal(false)}
                className="btn-primary w-full mt-8 py-3 font-semibold tracking-wide"
              >
                GOT IT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
